import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  type CitationParts,
  mlaDate,
  type PartialDate,
  parsePartialDate,
} from "@/lib/tool-kit/citation-parts";
import {
  closer,
  get,
  italic,
  mlaUrl,
  plain,
} from "@/lib/tool-kit/legal-sources/common";
import {
  bareCompany,
  firstWords,
  longDate,
  type MediaValues,
  possessive,
} from "@/lib/tool-kit/media-sources/common";

/*
 * Images made with generative AI — DALL·E and ChatGPT's image generation, Midjourney,
 * Adobe Firefly, Stable Diffusion, Imagen and Gemini — in APA 7, MLA 9 and Chicago 18.
 *
 * Sources checked (October 2026):
 * - APA Style blog, "Citing generative AI in APA Style: Part 1—Reference formats"
 *   (McAdoo, Denneny & Lee, September 9, 2025): when AI creates "tables, figures, or
 *   other visual representations—indicate that you used AI in the table or figure
 *   note" and cite the AI tool generally with the software template (§10.10):
 *   "AI Company Name. (year). Tool Name/Model in Italics and Title Case [Description;
 *   e.g., Large language model]. URL of the tool" — "use the description that best
 *   fits the type of AI model or tool used", the date is the year the tool was last
 *   updated, and the model name replaces a version number. Prompts are described in
 *   the text or a note, never in the reference. APA Style, "AI references" (February
 *   2026, updated April 2026) repeats the tool format. Part 2 of the series: images
 *   made with AI built into common software (Canva) need no reference.
 * - MLA Style Center, "How do I cite generative AI in MLA style? (Updated and
 *   Revised)" (August 13, 2025), example 3: "Fig. 1. “Create an expressionist-style
 *   image of two people standing on a beach looking at the ocean” prompt, DALL-E,
 *   version 3, OpenAI, 23 Sept. 2024, chatgpt.com/share/66f1c3a3-3f90-8000-9750-
 *   82c57c4a6592." The same elements make a works-cited entry; the tool is italic; the
 *   Version element names the model or version; a stable share link is preferred to
 *   the tool's general URL.
 * - Chicago Style Q&A, "How do you cite images generated by AI?" (18th ed.): credited
 *   like any image, with the prompt in quotation marks — "“A modern office rendered as
 *   a cubist painting,” image generated by OpenAI’s DALL·E 2, March 5, 2023." CMOS 18
 *   §14.112: AI-generated content is cited in the text or a note and, like personal
 *   communications, not usually in a bibliography (as quoted by the DePaul and
 *   Georgetown library guides).
 */

export const AI_IMAGE_FIELDS = {
  prompt: "prompt",
  tool: "tool",
  model: "model",
  company: "company",
  date: "date",
  shareUrl: "shareUrl",
  toolUrl: "toolUrl",
  toolYear: "toolYear",
  description: "description",
  figureNumber: "figureNumber",
} as const;

const F = AI_IMAGE_FIELDS;

/** APA's bracketed description when the user leaves it blank. */
export const DEFAULT_AI_IMAGE_DESCRIPTION = "Text-to-image model";

export interface AiImageLabels {
  apa: string;
  mla: string;
  chicago: string;
  figureNote: string;
  toolReference: string;
  parenthetical: string;
  narrative: string;
  caption: string;
  worksCited: string;
  inText: string;
  creditLine: string;
  footnote: string;
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

interface AiImage {
  prompt: string;
  tool: string;
  /** The tool and model as one name: "DALL·E 3", "Midjourney V7". */
  name: string;
  model: string;
  company: string;
  date: PartialDate | null;
  shareUrl: string;
  toolUrl: string;
  toolYear: string;
  description: string;
  figure: string;
}

const RE_TRAILING_PERIOD = /\.+$/;
const RE_ENDS_IN_MARK = /[?!]$/;
const RE_YEAR = /^\d{4}$/;
const RE_VERSION_NUMBER = /^(?:v(?:ersion)?\s*)?(\d+(?:\.\d+)*)$/i;
const RE_WORDS = /\s+/;

/** "DALL·E" + "3" → "DALL·E 3"; a model that already names the tool stands alone. */
function toolName(tool: string, model: string): string {
  if (model === "") {
    return tool;
  }
  const lastWord = tool.split(RE_WORDS).at(-1)?.toLowerCase() ?? "";
  if (lastWord && model.toLowerCase().includes(lastWord)) {
    return model;
  }
  return `${tool} ${model}`.trim();
}

function readImage(values: MediaValues): AiImage {
  const tool = get(values, F.tool);
  const model = get(values, F.model);
  const date = parsePartialDate(get(values, F.date));
  const toolYear = get(values, F.toolYear);
  let year = "n.d.";
  if (RE_YEAR.test(toolYear)) {
    year = toolYear;
  } else if (date) {
    year = String(date.year);
  }
  return {
    prompt: get(values, F.prompt).replace(RE_TRAILING_PERIOD, ""),
    tool,
    name: toolName(tool, model),
    model,
    // APA and MLA leave business words such as "Inc." out of a company's name.
    company: bareCompany(get(values, F.company)),
    date,
    shareUrl: get(values, F.shareUrl),
    toolUrl: get(values, F.toolUrl),
    toolYear: year,
    description: get(values, F.description) || DEFAULT_AI_IMAGE_DESCRIPTION,
    figure: get(values, F.figureNumber) || "1",
  };
}

/** “Prompt,” — the mark goes inside unless the prompt ends with its own ? or !. */
function quotedPrompt(prompt: string, mark: "," | "." | ""): string {
  if (mark === "" || RE_ENDS_IN_MARK.test(prompt)) {
    return `“${prompt}”`;
  }
  return `“${prompt}${mark}”`;
}

/* ---------------------------------------------------------------------- APA */

function apaFigureNote(image: AiImage): CitationParts {
  const credit = image.company ? ` (${image.company}, ${image.toolYear})` : "";
  return [
    italic("Note."),
    plain(
      ` Image generated by ${image.name}${credit} from the prompt ${quotedPrompt(image.prompt, ".")}`
    ),
  ];
}

function apaToolReference(image: AiImage): CitationParts {
  const description = ` [${image.description}].`;
  const url = image.toolUrl ? ` ${image.toolUrl}` : "";
  if (image.company === "") {
    // No company: the tool moves to the author position, before the date.
    return [
      italic(image.name),
      plain(`${description} (${image.toolYear}).${url}`),
    ];
  }
  return [
    plain(`${image.company}${closer(image.company)} (${image.toolYear}). `),
    italic(image.name),
    plain(`${description}${url}`),
  ];
}

function apaOutput(image: AiImage, labels: AiImageLabels): CitationOutput {
  const author = image.company || image.name;
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.figureNote, parts: apaFigureNote(image) },
      { label: labels.toolReference, parts: apaToolReference(image) },
      {
        label: labels.parenthetical,
        parts: [plain(`(${author}, ${image.toolYear})`)],
      },
      {
        label: labels.narrative,
        parts: [plain(`${author} (${image.toolYear})`)],
      },
    ],
    note: labels.apaNote,
  };
}

/* ---------------------------------------------------------------------- MLA */

/** MLA's Version element: "version 3" for a number, "model GPT Image 1" for a name. */
function mlaVersion(model: string): string {
  if (model === "") {
    return "";
  }
  const number = RE_VERSION_NUMBER.exec(model);
  return number ? `version ${number[1]}` : `model ${model}`;
}

/** ", version 3, OpenAI, 23 Sept. 2024, chatgpt.com/share/…." after the tool. */
function mlaDetails(image: AiImage): string {
  const link = image.shareUrl || image.toolUrl;
  const details = [
    mlaVersion(image.model),
    image.company,
    mlaDate(image.date),
    link ? mlaUrl(link) : "",
  ].filter(Boolean);
  return details.length > 0 ? `, ${details.join(", ")}.` : ".";
}

function mlaOutput(image: AiImage, labels: AiImageLabels): CitationOutput {
  const prompt = quotedPrompt(image.prompt, "");
  const details = mlaDetails(image);
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      {
        label: labels.caption,
        parts: [
          plain(`Fig. ${image.figure}. ${prompt} prompt, `),
          italic(image.tool),
          plain(details),
        ],
      },
      {
        label: labels.worksCited,
        parts: [
          plain(`${prompt} prompt. `),
          italic(image.tool),
          plain(details),
        ],
      },
      {
        label: labels.inText,
        parts: [plain(`(“${firstWords(image.prompt, 4)}”)`)],
      },
    ],
    note: labels.mlaNote,
  };
}

/* ------------------------------------------------------------------ Chicago */

/** "OpenAI’s DALL·E 3" — or "Midjourney V7" when the company and tool share a name. */
function chicagoGenerator(image: AiImage): string {
  const company = bareCompany(image.company);
  if (
    company === "" ||
    image.name.toLowerCase().startsWith(company.toLowerCase())
  ) {
    return image.name;
  }
  return `${possessive(company)} ${image.name}`;
}

function chicagoCredit(image: AiImage, withUrl: boolean): CitationParts {
  const pieces = [
    `image generated by ${chicagoGenerator(image)}`,
    longDate(image.date),
    withUrl ? image.shareUrl : "",
  ].filter(Boolean);
  return [plain(`${quotedPrompt(image.prompt, ",")} ${pieces.join(", ")}.`)];
}

function chicagoOutput(image: AiImage, labels: AiImageLabels): CitationOutput {
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [
      { label: labels.creditLine, parts: chicagoCredit(image, false) },
      { label: labels.footnote, parts: chicagoCredit(image, true) },
    ],
    note: labels.chicagoNote,
  };
}

export function aiImageIsReady(values: MediaValues): boolean {
  return get(values, F.prompt) !== "" && get(values, F.tool) !== "";
}

export function formatAiImage(
  values: MediaValues,
  labels: AiImageLabels
): CitationOutput[] {
  const image = readImage(values);
  return [
    apaOutput(image, labels),
    mlaOutput(image, labels),
    chicagoOutput(image, labels),
  ];
}
